import React, { useState, useEffect } from 'react';
import { THEME } from '../../utils/theme';
import api, { API_BASE } from '../../utils/api';
import Spinner from '../ui/Spinner';

const LoginScreen = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); // Security: default to session-only

  // Check URL for invite code on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('invite');
    if (code) {
      setInviteCode(code);
      setMode('register');
      validateInvite(code);
    }
  }, []);

  const validateInvite = async (code) => {
    try {
      const response = await fetch(`${API_BASE}/api/invites/${code}/validate`);
      const data = await response.json();
      if (data.valid) {
        setInviteInfo(data);
        setError('');
      } else {
        setInviteInfo(null);
        setError(data.error || 'Invalid invite code');
      }
    } catch (err) {
      setInviteInfo(null);
      setError('Failed to validate invite code');
    }
  };

  const handleInviteCodeChange = (code) => {
    setInviteCode(code);
    if (code.length >= 6) {
      validateInvite(code);
    } else {
      setInviteInfo(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const user = await api.login(username, password, rememberMe);
      onLogin(user);
    } catch (err) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!username || username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (!inviteInfo) {
      setError('Please enter a valid invite code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/invites/${inviteCode}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, display_name: displayName || username })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      // Log in with new credentials
      const user = await api.login(username, password);
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '16px 18px',
    color: THEME.text, fontSize: '15px', boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none'
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at top, ${THEME.bgElevated} 0%, ${THEME.bg} 50%, #050508 100%)` }}>
      <div style={{
        background: `linear-gradient(145deg, ${THEME.bgElevated} 0%, ${THEME.bgCard} 100%)`, borderRadius: '24px', padding: '48px', width: '100%', maxWidth: '420px',
        border: `1px solid ${THEME.border}`, boxShadow: `0 25px 80px ${THEME.shadowHeavy}`, animation: 'slideUp 0.4s ease'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
            <img src="/icons/icon.png" alt="" style={{ height: '80px', width: 'auto' }} />
            <h1 style={{ margin: 0, fontSize: '36px', fontWeight: '800', background: `linear-gradient(135deg, ${THEME.omega} 0%, ${THEME.supe} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>jasons.fun</h1>
          </div>
          <p style={{ margin: 0, color: THEME.textMuted, fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase' }}>Co-op Compendium</p>
        </div>

        {/* Mode Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: THEME.bg, padding: '4px', borderRadius: '10px' }}>
          <button onClick={() => setMode('login')} style={{
            flex: 1, background: mode === 'login' ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : 'transparent',
            color: mode === 'login' ? '#000' : THEME.textMuted, border: 'none', borderRadius: '8px', padding: '12px', fontWeight: '600', cursor: 'pointer'
          }}>Sign In</button>
          <button onClick={() => setMode('register')} style={{
            flex: 1, background: mode === 'register' ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : 'transparent',
            color: mode === 'register' ? '#000' : THEME.textMuted, border: 'none', borderRadius: '8px', padding: '12px', fontWeight: '600', cursor: 'pointer'
          }}>Join</button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '16px' }}>
              <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.omega; e.target.style.boxShadow = `0 0 0 3px ${THEME.successLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.supe; e.target.style.boxShadow = `0 0 0 3px ${THEME.infoLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', cursor: 'pointer', color: THEME.textMuted, fontSize: '13px' }}>
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: THEME.success, cursor: 'pointer' }} />
              <span>Remember me</span>
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: THEME.textDim }}>Stay signed in on this device</span>
            </label>
            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '20px', color: THEME.danger, fontSize: '14px', textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={loading} style={{
              width: '100%', background: `linear-gradient(135deg, ${THEME.omega} 0%, ${THEME.supe} 100%)`, color: '#000', border: 'none',
              borderRadius: '12px', padding: '16px', fontSize: '16px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1, boxShadow: `0 4px 20px ${THEME.successGlow}`
            }}>{loading ? <Spinner size={20} color="#000" /> : 'Sign In'}</button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase' }}>Invite Code</label>
              <input type="text" placeholder="Enter your invite code" value={inviteCode} onChange={(e) => handleInviteCodeChange(e.target.value.toUpperCase())} style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '2px', textAlign: 'center' }}
                onFocus={e => { e.target.style.borderColor = THEME.success; e.target.style.boxShadow = `0 0 0 3px rgba(16,185,129,0.2)`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
              {inviteInfo && (
                <div style={{ background: `${THEME.success}15`, border: `1px solid ${THEME.success}30`, borderRadius: '8px', padding: '10px', marginTop: '8px', fontSize: '13px', color: THEME.success }}>
                  Valid invite to join <strong>{inviteInfo.squad_name}</strong> from {inviteInfo.created_by}
                </div>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase' }}>Username</label>
              <input type="text" placeholder="Choose a username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.omega; e.target.style.boxShadow = `0 0 0 3px ${THEME.successLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase' }}>Display Name</label>
              <input type="text" placeholder="Your display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.omega; e.target.style.boxShadow = `0 0 0 3px ${THEME.successLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase' }}>Password</label>
              <input type="password" placeholder="Create a password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.supe; e.target.style.boxShadow = `0 0 0 3px ${THEME.infoLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase' }}>Confirm Password</label>
              <input type="password" placeholder="Confirm your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle}
                onFocus={e => { e.target.style.borderColor = THEME.supe; e.target.style.boxShadow = `0 0 0 3px ${THEME.infoLight}`; }}
                onBlur={e => { e.target.style.borderColor = THEME.border; e.target.style.boxShadow = 'none'; }} />
            </div>
            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '20px', color: THEME.danger, fontSize: '14px', textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={loading || !inviteInfo} style={{
              width: '100%', background: inviteInfo ? `linear-gradient(135deg, ${THEME.omega} 0%, ${THEME.supe} 100%)` : THEME.bgHover, color: inviteInfo ? '#000' : THEME.textMuted, border: 'none',
              borderRadius: '12px', padding: '16px', fontSize: '16px', fontWeight: '700', cursor: (loading || !inviteInfo) ? 'not-allowed' : 'pointer',
              opacity: (loading || !inviteInfo) ? 0.7 : 1, boxShadow: inviteInfo ? `0 4px 20px ${THEME.successGlow}` : 'none'
            }}>{loading ? <Spinner size={20} color="#000" /> : 'Create Account'}</button>
          </form>
        )}
        {mode === 'login' && <p style={{ color: THEME.textDim, fontSize: '12px', textAlign: 'center', marginTop: '28px' }}>Existing users only. Need an invite? Ask a member!</p>}
      </div>
    </div>
  );
};

export default LoginScreen;
